import { v4 as uuid } from 'uuid';

import { calculateWeight, sortByWeight } from '@techchange/dnd';

import { CONTINUATION_TYPES } from '../../../../../constants';
import {
	getAdvancedSettings,
	findAdvancedSettingIndices,
} from '../../../../../utils';
import {
	FORM_EDITOR_FIELDS,
	SECTION_EDITOR_FIELDS,
} from '../constants';

export const FORM_SECTION_DATA_DISPATCH = {
	ADD_FORM_SECTION: 'ADD_FORM_SECTION',
	CHANGE_FORM_SECTION_ADVANCED_SETTINGS: 'CHANGE_FORM_SECTION_ADVANCED_SETTINGS',
	DELETE_FORM_SECTION: 'DELETE_FORM_SECTION',
	MOVE_FORM_SECTION: 'MOVE_FORM_SECTION',
	UPDATE_FORM_SECTION: 'UPDATE_FORM_SECTION',
	UPDATE_FORM_SECTION_CONTENTS: 'UPDATE_FORM_SECTION_CONTENTS',
};

/**
 * Set of functions that update the form sections state for a given action type.
 */
export const formSectionDataReducers = {
	[FORM_SECTION_DATA_DISPATCH.ADD_FORM_SECTION]: addFormSection,
	[FORM_SECTION_DATA_DISPATCH.CHANGE_FORM_SECTION_ADVANCED_SETTINGS]: changeSectionAdvancedSetting,
	[FORM_SECTION_DATA_DISPATCH.DELETE_FORM_SECTION]: deleteFormSection,
	[FORM_SECTION_DATA_DISPATCH.MOVE_FORM_SECTION]: moveFormSection,
	[FORM_SECTION_DATA_DISPATCH.UPDATE_FORM_SECTION]: updateFormSection,
	[FORM_SECTION_DATA_DISPATCH.UPDATE_FORM_SECTION_CONTENTS]: updateFormSectionContents,
};

/**
 * Handles adding a new section to the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action                  Data related to the requested action.
 * @param {Array}  action.tickets          The list of tickets used to build out default
 *                                          advanced navigation settings.
 * @param {Array}  action.ticketGroups     The list of ticket groups used to build out
 *                                          default advanced navigation settings.
 * @param {String} action.defaultLanguage  The default language for the form.
 *
 * @returns {Object} result  The updated form data.
 */
export function addFormSection(formData, action) {

	const { tickets, ticketGroups, defaultLanguage } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the maximum weight for the existing sections
	const prev = Math.max(
		...result[FORM_EDITOR_FIELDS.SECTIONS].map((section) => section[SECTION_EDITOR_FIELDS.WEIGHT]),
		0,
	);

	const sectionID = uuid();

	let advancedSettings = [];

	// Build out default advanced settings based on tickets and ticket groups
	if (tickets && ticketGroups) {

		advancedSettings = getAdvancedSettings(
			sectionID,
			[],
			tickets,
			ticketGroups
		);

	}

	const sectionData = {
		[SECTION_EDITOR_FIELDS.ID]: sectionID,
		[SECTION_EDITOR_FIELDS.CONTINUATION_TYPE]: CONTINUATION_TYPES.DEFAULT,
		[SECTION_EDITOR_FIELDS.CONTINUATION_SECTION_ID]: null,
		[SECTION_EDITOR_FIELDS.WEIGHT]: calculateWeight(prev, null),
		[SECTION_EDITOR_FIELDS.FIELDS]: [],
		[SECTION_EDITOR_FIELDS.ADVANCED_SETTINGS]: advancedSettings,
		[SECTION_EDITOR_FIELDS.CONTENTS]: [{
			language: defaultLanguage,
			[SECTION_EDITOR_FIELDS.TITLE]: null,
			[SECTION_EDITOR_FIELDS.DESCRIPTION]: null,
		}],
		[SECTION_EDITOR_FIELDS.DELETED_CONTENTS]: [],
	};

	// Append the new form section to the form
	result[FORM_EDITOR_FIELDS.SECTIONS].push(sectionData);

	// Sort the form sections (by weight) for the form
	result[FORM_EDITOR_FIELDS.SECTIONS] = sortByWeight(result[FORM_EDITOR_FIELDS.SECTIONS]);

	return result;

}

/**
 * Handles changes to an advanced section setting in the form.
 *
 * @param {Object} formData           The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.settingID   The ID of the advanced setting to update.
 * @param {String} action.fieldName   The name of the field to update for the advanced setting.
 * @param {String} action.fieldValue  The new value of the field to update for the advanced setting.
 *
 * @returns {Object}  The updated form state.
 */
export function changeSectionAdvancedSetting(formData, action) {

	const { settingID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the advanced setting from the nested state
	const [originalSectionIdx, originalSettingIdx] =
		findAdvancedSettingIndices(result[FORM_EDITOR_FIELDS.SECTIONS], settingID);

	// Find the existing position/data for the advanced setting

	const prevSection = result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx];

	const prevAdvancedSettings = prevSection[SECTION_EDITOR_FIELDS.ADVANCED_SETTINGS];

	// Update the data for the advanced setting
	prevAdvancedSettings[originalSettingIdx] = {
		...prevAdvancedSettings[originalSettingIdx],
		[fieldName]: fieldValue,
	};

	return result;

}

/**
 * Handles deleting a form section from the form.
 *
 * @param {Object} formData           The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.sectionID   The ID of the section to delete.
 *
 * @returns {Object}  The updated form state.
 */
export function deleteFormSection(formData, action) {

	const { sectionID } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the existing position of the section
	const originalSectionIdx =
		result[FORM_EDITOR_FIELDS.SECTIONS]
			.findIndex((section) => section.id === sectionID);

	// Remove the section data (and nested fields) from the state
	result[FORM_EDITOR_FIELDS.SECTIONS].splice(originalSectionIdx, 1);

	return result;

}

/**
 * Handles changes to the position of a form section in the form.
 *
 * @param {Object} formData  The current form data state.
 *
 * @param {Object} action            Data related to the requested action.
 * @param {string} action.sectionID  The id of the form section to move.
 * @param {object} action.prevItem   The item to place the section after.
 * @param {object} action.nextItem   The item to place the section before.
 *
 * @returns {Object}  The updated form state.
 */
export function moveFormSection(formData, action) {

	const { sectionID, prevItem, nextItem } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the index of the "moved" section in the list of form sections
	const index =
		result[FORM_EDITOR_FIELDS.SECTIONS]
			.findIndex((section) => section.id === sectionID);

	let prev, next;

	// Find the previous section (if any)
	if (prevItem) {

		prev = result[FORM_EDITOR_FIELDS.SECTIONS]
			.find((section) => section.id === prevItem.id);

	}

	// Find the next section (if any)
	if (nextItem) {

		next = result[FORM_EDITOR_FIELDS.SECTIONS]
			.find((section) => section.id === nextItem.id);

	}

	// Check that at least either the previous or next section is valid
	if (!prev && !next) {
		return result;
	}

	const prevWeight = prevItem?.[SECTION_EDITOR_FIELDS.WEIGHT];
	const nextWeight = nextItem?.[SECTION_EDITOR_FIELDS.WEIGHT];

	// Calculate the new weight of the "moved" section
	const weight = calculateWeight(prevWeight, nextWeight);

	// Update the data for the section
	result[FORM_EDITOR_FIELDS.SECTIONS][index] = {
		...result[FORM_EDITOR_FIELDS.SECTIONS][index],
		[SECTION_EDITOR_FIELDS.WEIGHT]: weight,
	};

	// Sort the form sections (by weight) for the form
	result[FORM_EDITOR_FIELDS.SECTIONS] = sortByWeight(result[FORM_EDITOR_FIELDS.SECTIONS]);

	return result;

}

/**
 * Updates the specified field of a form section with the provided value.
 *
 * @param {Object} formData           The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.sectionID   The ID of the section to update.
 * @param {String} action.fieldName   The name of the field to update for the section.
 * @param {String} action.fieldValue  The new value of the field to update for the section.
 *
 * @returns {Object} result  The updated form state.
 */
function updateFormSection(formData, action) {

	const { sectionID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	// Find the existing position/data of the section

	const originalSectionIdx =
		result[FORM_EDITOR_FIELDS.SECTIONS]
			.findIndex((section) => section.id === sectionID);

	// Update the data for the section
	result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx] = {
		...result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx],
		[fieldName]: fieldValue,
	};

	return result;

}

/**
 * Determines the updated state to reflect the field value for an
 * attribute of a form section content object.
 *
 * @param {Object} formData           The current form data state.
 *
 * @param {Object} action             Data related to the requested action.
 * @param {String} action.sectionID   The ID of the section to update.
 * @param {String} action.fieldName   The name of the field to update for the section.
 * @param {String} action.fieldValue  The new value of the field to update for the section.
 *
 * @returns {Object}  The updated form state.
 */
function updateFormSectionContents(formData, action) {

	const { sectionID, fieldName, fieldValue } = action;

	// Create a (deep) copy of the form data
	const result = structuredClone(formData);

	const selectedLanguage = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Find the existing position/data of the section

	const originalSectionIdx =
		result[FORM_EDITOR_FIELDS.SECTIONS]
			.findIndex((section) => section.id === sectionID);

	const originalSectionData = result[FORM_EDITOR_FIELDS.SECTIONS][originalSectionIdx];

	// Get the existing multi-language content for the section
	const updatedSectionContents = originalSectionData[SECTION_EDITOR_FIELDS.CONTENTS];

	// Find the translated content for the current language, if any
	const activeTranslationIdx = updatedSectionContents
		.findIndex((content) => content.language === selectedLanguage);

	// Update the multi-language content for the section
	if (activeTranslationIdx >= 0) {

		updatedSectionContents[activeTranslationIdx] = {
			...updatedSectionContents[activeTranslationIdx],
			[fieldName]: fieldValue,
		};

	}

	return result;

}
